import { Component, OnInit, Inject, HostListener, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatRadioChange, MatSnackBar, MatTableDataSource } from '@angular/material';
import { Business } from '../models/business.model';
import { BusinessHours } from '../models/business-hours.model';
import { BusinessTag } from '../models/business-tag.model';
import { TagService } from '../../shared/services/tag.service';
import { Tag } from '../../shared/models/tag.model';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { CanDeactivateComponent } from '../../shared/services/can-deactivate-guard.service';
import { Observable } from 'rxjs';
import { BusinessHoursTableComponent } from '../business-hours-table/business-hours-table.component';


@Component({
  selector: 'app-edit-business-hours',
  templateUrl: './edit-business-hours.component.html',
  styleUrls: ['./edit-business-hours.component.scss']
})
export class EditBusinessHoursComponent implements OnInit, CanDeactivateComponent {

  @ViewChild('businessHoursTable') bHrsTble: BusinessHoursTableComponent;
  hasUnsavedChanges: Observable<boolean>;
  disableClose = false;
  isRadioGroupDisabled = false;
  dataSource: MatTableDataSource<any>;
  title = '';
  business: Business;
  allTags: BusinessTag[];
  hoursTags: BusinessTag[];
  selectedHourTag: BusinessTag = null;
  businessHours: Array<BusinessHours>;
  hoursExceptions: string;

  constructor(private _matDialogRef: MatDialogRef<EditBusinessHoursComponent>,
    private _tagService: TagService,
    private _businessManagerService: BusinessManagerService,
    public snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) data) {
    this.title = data.title;
    this.business = data.business;
  }

  async ngOnInit() {
    this.setSelectedHourTag();
    this.setBusinessHours();
    this.setHoursExceptions();
    this.setHoursTags();
    this._matDialogRef.backdropClick().subscribe(
      () => this.closeDialog());
  }

  //#region ngOnInit set up

  setHoursTags(): void {
    this._tagService.getAllTags().then(
      (tags) => {
        this.allTags = tags;
        this.hoursTags = this.allTags.filter(this.isHoursBusinessTag);
      });
  }

  setSelectedHourTag(): void {
    const hoursTags = this.business.tags.filter(this.isHoursBusinessTag);
    if (hoursTags.length > 0) {
      this.selectedHourTag = hoursTags.pop();
    }
  }

  setBusinessHours(): void {
    this.businessHours = this.business.hours;
  }

  setHoursExceptions(): void {
    this.hoursExceptions = this.business.hoursExceptions;
  }

  //#endregion

  isHoursBusinessTag(element): boolean {
    return (/^Hours::.+/.test(element.tagName));
  }

  isDisplayHoursRadioBtnSelected(hour: any): boolean {
    if (!hour) {
      return false;
    } else if (this.selectedHourTag === null) {
      return hour.tagName === 'customHours';
    } else {
      return hour.tagName === this.selectedHourTag.tagName;
    }
  }

  async displayHoursRadioBtnChanged(radioChange: MatRadioChange) {
    this.diableCloseBtn();
    this.disableRadioGrp();
    if (radioChange.value === 'customHours') { // User selects customHours
      this.updateRadioBtnTagStatus(this.selectedHourTag, true)
        .then(() => {
          this.selectedHourTag = null;
          this.reEnableCloseBtn();
          this.reEnableRadioGrp();
        });
    } else {
      const newHoursTagSelection = this.allTags.filter(tag => tag.tagName.includes(radioChange.value)).pop();
      if (!this.selectedHourTag) { // Aka customHours was the previous value
        this.updateRadioBtnTagStatus(newHoursTagSelection, false)
          .then(() => {
            this.setSelectedHourTag();
            this.reEnableCloseBtn();
            this.reEnableRadioGrp();
          });
      } else { // All other cases
        this.updateRadioBtnTagStatus(this.selectedHourTag, true)
          .then(() => this.updateRadioBtnTagStatus(newHoursTagSelection, false)
            .then(() => {
              this.setSelectedHourTag();
              this.reEnableCloseBtn();
              this.reEnableRadioGrp();
            }));
      }
    }
  }

  async updateRadioBtnTagStatus(tag: Tag, shouldDelete: boolean) {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    try {
      const tagId = tag.tagId;
      if (shouldDelete) {
        await this._tagService.deleteTag(tagId, this.business.id);
      } else {
        await this._tagService.postTag(tagId, this.business.id);
      }
      const newBusiness = await this._businessManagerService.updateBusiness(this.business);
      Object.assign(this.business, newBusiness);
    } catch (e) {
      this.snackBar.open(ERROR_MSG, 'Error', {
        duration: 6000
      });
      throw e;
    }
  }

  async updateHoursException(hrExceptions: string) {
    this.diableCloseBtn();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    try {
      this.business.closedHolidays = hrExceptions;
      const newBusiness = await this._businessManagerService.updateBusiness(this.business);
      Object.assign(this.business, newBusiness);
    } catch (e) {
      this.snackBar.open(ERROR_MSG, 'Error', {
        duration: 6000
      });
      throw e;
    } finally {
      this.reEnableCloseBtn();
    }
  }

  async close() {
    if (this.business) {
      this._matDialogRef.close(true);
    } else {
      const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
      try {
        const newBusiness = await this._businessManagerService.updateBusiness(this.business);
        Object.assign(this.business, newBusiness);
      } catch (e) {
        this.snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    }
  }

  async closeDialog() {
    if (this.canDeactivate()) {
      this.close();
    } else {
      if (confirm('You Have Unsaved Changes. Press Cancel to go back and save changes or Ok to discard changes.')) {
        this.close();
      }
    }
  }

  disableRadioGrp(): void {
    this.isRadioGroupDisabled = true;
  }

  reEnableRadioGrp(): void {
    this.isRadioGroupDisabled = false;
  }

  diableCloseBtn(): void {
    this.disableClose = true;
  }

  reEnableCloseBtn(): void {
    this.disableClose = false;
  }

  @HostListener('window:beforeunload')
  canDeactivate() {
    return (!this.bHrsTble || !this.bHrsTble.hasUnsavedChanges())
      && this.hoursExceptions === this.business.hoursExceptions;
  }
}
